import React, { useEffect, useMemo, useRef, useState } from 'react';
import DOMPurify from 'isomorphic-dompurify';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  FaBolt,
  FaBoxOpen,
  FaCheckCircle,
  FaDownload,
  FaFacebookF,
  FaFileAlt,
  FaHeart,
  FaLink,
  FaMinus,
  FaPencilAlt,
  FaPlus,
  FaRegHeart,
  FaShareAlt,
  FaShieldAlt,
  FaStar,
  FaTag,
  FaTimes,
  FaTruck,
  FaTwitter,
  FaUndoAlt,
  FaWhatsapp,
} from 'react-icons/fa';
import { IoIosArrowForward } from 'react-icons/io';
import { MdLocalShipping, MdVerified, MdOutlinePlaylistAdd } from 'react-icons/md';
import { RiShoppingCartLine } from 'react-icons/ri';
import Rating from '../components/Rating';
import Reviews from '../components/Reviews';
import { useDispatch, useSelector } from 'react-redux';
import { product_details, get_reviews } from '../store/reducers/homeReducer';
import toast from 'react-hot-toast';
import { add_to_card, add_to_wishlist, messageClear } from '../store/reducers/cardReducer';
import { add_to_my_list, messageClear as myListMessageClear } from '../store/reducers/myListReducer';
import { fetch_shipping_info } from '../store/reducers/shippingReducer';
import { useCurrency } from '../context/CurrencyContext';
import { openEnquiry } from '../utils/enquiry';
import PageSeo from '../components/seo/PageSeo';
import { useProductSeo } from '../hooks/useSeo';

const FREE_SHIPPING_THRESHOLD = 200;

// ── Utilities ────────────────────────────────────────────────────────────────

const useCountdown = (initialSeconds = 18000) => {
  const [time, setTime] = useState(initialSeconds);
  useEffect(() => {
    const timer = setInterval(() => setTime((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(timer);
  }, []);
  const h = String(Math.floor(time / 3600)).padStart(2, '0');
  const m = String(Math.floor((time % 3600) / 60)).padStart(2, '0');
  const s = String(time % 60).padStart(2, '0');
  return `${h}:${m}:${s}`;
};

const getEmbedUrl = (url = '') => {
  if (!url) return null;
  const ytMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([A-Za-z0-9_-]+)/);
  if (ytMatch?.[1]) return `https://www.youtube.com/embed/${ytMatch[1]}`;
  const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
  if (vimeoMatch?.[1]) return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
  return null;
};

const getFinalPrice = (price = 0, discount = 0) =>
  discount ? price - (price * discount) / 100 : price;

const getProductImage = (product) =>
  product?.images?.[0] || product?.image || '/images/no-image.png';

const stripHtml = (html = '') => {
  const withoutTags = DOMPurify.sanitize(html, { ALLOWED_TAGS: [] });
  if (typeof document === 'undefined') return withoutTags.replace(/\s+/g, ' ').trim();
  const el = document.createElement('textarea');
  el.innerHTML = withoutTags;
  return el.value.replace(/\s+/g, ' ').trim();
};

const getRatingLabel = (rating) => {
  if (rating >= 5) return { label: 'Excellent', color: 'text-orange-500' };
  if (rating >= 4) return { label: 'Good', color: 'text-green-600' };
  if (rating >= 3) return { label: 'Average', color: 'text-yellow-500' };
  if (rating >= 2) return { label: 'Poor', color: 'text-orange-600' };
  return { label: 'Terrible', color: 'text-red-600' };
};

// ── Small shared components ──────────────────────────────────────────────────

const QuantitySelector = ({ quantity, inc, dec }) => (
  <div className="grid shrink-0 grid-cols-[32px_40px_32px] overflow-hidden rounded-full border border-slate-200 bg-white">
    <button
      type="button"
      onClick={dec}
      className="flex h-8 items-center justify-center text-slate-600 transition hover:bg-slate-50 hover:text-[#E21C1B]"
      aria-label="Decrease quantity"
    >
      <FaMinus size={10} />
    </button>
    <span className="flex h-8 items-center justify-center text-sm font-bold text-slate-900">
      {quantity}
    </span>
    <button
      type="button"
      onClick={inc}
      className="flex h-8 items-center justify-center text-slate-600 transition hover:bg-slate-50 hover:text-[#E21C1B]"
      aria-label="Increase quantity"
    >
      <FaPlus size={10} />
    </button>
  </div>
);

// ── Share button (native share sheet, falls back to a platform dropdown) ────

const ShareButton = ({ product, compact = false }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';
  const shareText = product?.name ? `Check out ${product.name} on Pikko Dubai` : 'Check out this product on Pikko Dubai';

  const platforms = [
    { Icon: FaFacebookF, href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, label: 'Facebook' },
    { Icon: FaTwitter, href: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`, label: 'Twitter / X' },
    { Icon: FaWhatsapp, href: `https://wa.me/?text=${encodeURIComponent(`${shareText} ${shareUrl}`)}`, label: 'WhatsApp' },
  ];

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Link copied to clipboard');
    } catch {
      toast.error('Could not copy link');
    }
    setOpen(false);
  };

  const handleClick = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: product?.name, text: shareText, url: shareUrl });
      } catch {
        // user dismissed the native share sheet — nothing to do
      }
      return;
    }
    setOpen((o) => !o);
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={handleClick}
        className={
          compact
            ? 'flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 text-slate-500 transition hover:bg-[#E21C1B] hover:text-white'
            : 'flex items-center gap-1.5 rounded-full border border-slate-200 px-3.5 py-1.5 text-xs font-bold text-slate-600 transition hover:border-[#E21C1B] hover:text-[#E21C1B]'
        }
        aria-label="Share this product"
      >
        <FaShareAlt size={compact ? 12 : 11} /> {!compact && 'Share'}
      </button>

      {open && (
        <div className="absolute right-0 top-full z-20 mt-2 w-48 overflow-hidden rounded-xl border border-slate-100 bg-white py-1.5 shadow-[0_12px_32px_rgba(15,23,42,0.14)]">
          {platforms.map(({ Icon, href, label }) => (
            <a
              key={label}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setOpen(false)}
              className="flex items-center gap-2.5 px-3.5 py-2 text-xs font-semibold text-slate-700 transition hover:bg-slate-50"
            >
              <Icon className="text-slate-400" size={13} /> {label}
            </a>
          ))}
          <button
            type="button"
            onClick={copyLink}
            className="flex w-full items-center gap-2.5 px-3.5 py-2 text-xs font-semibold text-slate-700 transition hover:bg-slate-50"
          >
            <FaLink className="text-slate-400" size={12} /> Copy link
          </button>
        </div>
      )}
    </div>
  );
};

const PromoStrip = ({ countdown }) => (
  <div className="bg-slate-950 text-white">
    <div className="mx-auto flex w-[94%] max-w-[1500px] items-center gap-5 overflow-hidden py-2.5 text-xs">
      <span className="flex shrink-0 items-center gap-1.5 font-semibold">
        <FaTruck className="text-emerald-400" /> Free shipping available
      </span>
      <span className="hidden shrink-0 items-center gap-1.5 font-semibold mds:flex">
        <FaShieldAlt className="text-sky-400" /> Secure checkout
      </span>
      <span className="hidden shrink-0 items-center gap-1.5 font-semibold lgs:flex">
        <FaTag className="text-amber-400" /> Exclusive Pikko Dubai offers
      </span>
      <span className="ml-auto flex shrink-0 items-center gap-1.5 font-semibold text-white/70">
        <FaBolt className="text-amber-400" /> Ends in <span className="font-mono text-white">{countdown}</span>
      </span>
    </div>
  </div>
);

const Breadcrumbs = ({ product }) => (
  <div className="border-b border-slate-100 bg-white">
    <div className="mx-auto flex w-[94%] max-w-[1500px] items-center gap-1.5 overflow-hidden py-3 text-sm text-slate-500">
      <Link to="/" className="shrink-0 transition hover:text-[#E21C1B]">Home</Link>
      <IoIosArrowForward className="shrink-0 text-xs text-slate-300" />
      <Link to="/" className="shrink-0 transition hover:text-[#E21C1B]">{product?.category || 'Products'}</Link>
      <IoIosArrowForward className="shrink-0 text-xs text-slate-300" />
      <span className="truncate font-medium text-slate-700">{product?.name || 'Product details'}</span>
    </div>
  </div>
);

// ── Reviews Modal ────────────────────────────────────────────────────────────

const ReviewsModal = ({ product, open, onClose }) => {
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 backdrop-blur-sm mds:items-center mds:p-6"
      onClick={onClose}
    >
      <div
        className="flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden rounded-t-2xl bg-white shadow-2xl mds:rounded-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex shrink-0 items-center justify-between border-b border-slate-100 px-5 py-4">
          <div>
            <h2 className="text-lg font-bold text-slate-950">Customer Reviews</h2>
            <p className="mt-0.5 text-xs text-slate-400">{product?.reviews?.length || 0} verified ratings</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-9 w-9 items-center justify-center rounded-full bg-slate-100 text-slate-600 transition hover:bg-red-50 hover:text-[#E21C1B]"
            aria-label="Close reviews"
          >
            <FaTimes size={13} />
          </button>
        </div>
        {/* Scrollable review list only — no write form */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          <Reviews product={product} hideReviewForm={true} previewCount={1000} />
        </div>
      </div>
    </div>
  );
};

// ── LEFT: Product Gallery ────────────────────────────────────────────────────

const ProductGallery = ({ product, images, image, setImage, hasDiscount, discount, isLowStock, addWishlist }) => {
  const allImages = (images?.length ? images : [image]).filter(Boolean);

  return (
    <div className="bg-white">
      {/* Desktop: thumbs left | main image right */}
      <div className="flex">
        {/* Vertical thumbnails — hidden on mobile */}
        <div className="scrollbar-hide hidden w-[74px] shrink-0 flex-col gap-2 overflow-y-auto pr-2.5 mds:flex" style={{ maxHeight: '520px' }}>
          {allImages.map((img, i) => (
            <button
              type="button"
              key={i}
              onClick={() => setImage(img)}
              className={`h-[68px] w-[68px] shrink-0 overflow-hidden rounded-md border transition-all duration-150 ${
                image === img
                  ? 'border-slate-900'
                  : 'border-slate-200 hover:border-slate-400'
              }`}
              aria-label={`View image ${i + 1}`}
            >
              <img src={img} alt="" className="h-full w-full object-cover" />
            </button>
          ))}
        </div>

        {/* Main image — strict 1:1 square, uniform across all products */}
        <div className="group relative flex-1">
          <div className="aspect-square w-full overflow-hidden bg-white">
            {hasDiscount && (
              <span className="absolute left-0 top-3 z-10 rounded-r-md bg-[#E21C1B] px-2.5 py-1 text-xs font-bold text-white">
                -{discount}% OFF
              </span>
            )}
            {isLowStock && (
              <span className="absolute right-0 top-3 z-10 rounded-l-md bg-slate-900 px-2.5 py-1 text-xs font-bold text-white">
                Almost sold out
              </span>
            )}
            <img
              src={image || getProductImage(product)}
              alt={product?.name || 'Product'}
              className="h-full w-full object-contain transition-transform duration-500 group-hover:scale-[1.03]"
            />
          </div>
        </div>
      </div>

      {/* Horizontal thumbnails — mobile only */}
      <div className="scrollbar-hide flex gap-2 overflow-x-auto pb-1 pt-2 mds:hidden">
        {allImages.map((img, i) => (
          <button
            type="button"
            key={i}
            onClick={() => setImage(img)}
            className={`h-[60px] w-[60px] shrink-0 overflow-hidden rounded-md border transition-all ${
              image === img ? 'border-slate-900' : 'border-slate-200 hover:border-slate-400'
            }`}
          >
            <img src={img} alt="" className="h-full w-full object-cover" />
          </button>
        ))}
      </div>

      {/* Bottom actions */}
      <div className="flex items-center gap-3 py-3">
        <button
          type="button"
          onClick={addWishlist}
          className="flex items-center gap-1.5 text-xs font-bold text-slate-500 transition hover:text-[#E21C1B]"
        >
          <FaHeart className="text-[#E21C1B]" size={12} /> Save to wishlist
        </button>
        <div className="ml-auto">
          <ShareButton product={product} />
        </div>
      </div>
    </div>
  );
};

// ── Review card (Temu-style) ─────────────────────────────────────────────────

const ReviewCard = ({ r }) => {
  const { label, color } = getRatingLabel(r.rating || 0);
  const initial = (r.name || 'A').charAt(0).toUpperCase();
  return (
    <div className="border-b border-slate-100 py-4 last:border-0">
      <div className="mb-2 flex items-center gap-2.5">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-200 text-sm font-bold text-slate-600">
          {initial}
        </div>
        <span className="text-sm font-semibold text-slate-800">{r.name || 'Anonymous'}</span>
        {r.date && (
          <span className="ml-auto shrink-0 text-xs text-slate-400">on {r.date}</span>
        )}
      </div>
      <div className="mb-2 flex items-center gap-2">
        <div className="flex gap-0.5">
          {[1, 2, 3, 4, 5].map((s) => (
            <FaStar key={s} size={12} className={s <= Math.round(r.rating || 0) ? 'text-yellow-400' : 'text-slate-200'} />
          ))}
        </div>
        <span className={`text-xs font-bold ${color}`}>{label}</span>
      </div>
      <p className="text-sm leading-relaxed text-slate-600">{r.review}</p>
    </div>
  );
};

// ── LEFT: Reviews preview section (Temu-style) ───────────────────────────────

const RatingSummarySection = ({ product, onSeeAll }) => {
  const dispatch = useDispatch();
  const { reviews = [], totalReview = 0 } = useSelector((state) => state.home);
  const reviewCount = totalReview || product?.reviews?.length || 0;
  const rating = product?.rating || 0;

  useEffect(() => {
    if (product?._id) {
      dispatch(get_reviews({ productId: product._id, pageNumber: 1 }));
    }
  }, [product?._id, dispatch]);

  return (
    <div className="mt-6 border-t border-slate-100 pt-6">
      {/* Section header */}
      <h2 className="text-base font-bold text-slate-900">
        Customer Reviews
        <span className="ml-2 text-sm font-semibold text-slate-400">({reviewCount})</span>
      </h2>

      {/* Rating summary row */}
      <div className="flex items-center gap-5 border-b border-slate-100 py-4">
        {/* Big score */}
        <div className="flex flex-col items-center">
          <span className="text-5xl font-bold leading-none text-slate-900">{rating.toFixed(1)}</span>
          <span className="mt-1.5 flex"><Rating ratings={rating} /></span>
          <span className="mt-1 text-xs text-slate-400">{reviewCount} reviews</span>
        </div>

        {/* Breakdown bars */}
        <div className="flex-1 space-y-1.5">
          {[5, 4, 3, 2, 1].map((star) => {
            const count = product?.reviews?.filter((r) => Math.round(r.rating) === star).length || 0;
            const pct = reviewCount > 0 ? (count / reviewCount) * 100 : 0;
            return (
              <div key={star} className="flex items-center gap-2 text-xs">
                <span className="w-3 shrink-0 text-right font-semibold text-slate-500">{star}</span>
                <FaStar className="shrink-0 text-yellow-400" size={9} />
                <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-slate-100">
                  <div className="h-full rounded-full bg-yellow-400 transition-all duration-500" style={{ width: `${pct}%` }} />
                </div>
                <span className="w-5 shrink-0 text-slate-400">{count}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Preview — first 3 reviews in Temu style */}
      <div>
        {reviews.length > 0 ? (
          reviews.slice(0, 3).map((r, i) => <ReviewCard key={i} r={r} />)
        ) : (
          <p className="py-6 text-center text-sm italic text-slate-400">No reviews yet.</p>
        )}
      </div>

      {/* See all reviews — only shown when reviews exist */}
      {reviewCount > 0 && (
        <div className="pt-3">
          <button
            type="button"
            onClick={onSeeAll}
            className="flex w-full items-center justify-center gap-2 rounded-full border border-slate-300 py-2.5 text-sm font-bold text-slate-700 transition hover:border-slate-900 hover:text-slate-900"
          >
            See all {reviewCount} reviews →
          </button>
        </div>
      )}
    </div>
  );
};

// ── LEFT: All product images stacked (Temu-style) ────────────────────────────

const ProductImagesStack = ({ images }) => {
  const [expanded, setExpanded] = useState(false);
  if (!images?.length) return null;

  const visible = expanded ? images : images.slice(0, 2);
  const hasMore = images.length > 2;

  return (
    <div className="mt-6 border-t border-slate-100 pt-6">
      <p className="mb-3 text-sm font-bold text-slate-900">
        Product Photos <span className="ml-1 font-semibold text-slate-400">({images.length})</span>
      </p>
      <div className="space-y-3">
        {visible.map((img, idx) => (
          <div key={idx} className="w-full overflow-hidden bg-white">
            <img
              src={img}
              alt={`Product view ${idx + 1}`}
              loading="lazy"
              className="block w-full object-contain"
            />
          </div>
        ))}
      </div>
      {hasMore && (
        <button
          type="button"
          onClick={() => setExpanded((e) => !e)}
          className="mt-3 flex w-full items-center justify-center gap-2 border-t border-slate-100 py-3.5 text-sm font-bold text-slate-700 transition hover:text-[#E21C1B]"
        >
          {expanded ? 'Show less ↑' : `See all ${images.length} photos ↓`}
        </button>
      )}
    </div>
  );
};

// ── LEFT: Content (description, video, images, specs, docs) ──────────────────

const LeftSpecifications = ({ product }) => {
  const specs = [
    ['Brand', product?.brand],
    ['Category', product?.category],
    ['Material', product?.material],
    ['Color', product?.color],
    ['Size', product?.size],
    ['Inventory', product?.inventory],
    ['Minimum order', product?.minOrderQty],
  ].filter(([, v]) => v !== undefined && v !== null && v !== '');

  if (!specs.length) return <p className="text-sm text-slate-500">Specifications will be updated soon.</p>;

  return (
    <div className="grid gap-x-6 mds:grid-cols-2">
      {specs.map(([label, value]) => (
        <div key={label} className="flex items-center justify-between gap-3 border-b border-slate-100 py-2.5 text-sm">
          <span className="font-semibold text-slate-500">{label}</span>
          <span className="text-right font-bold text-slate-900">{value}</span>
        </div>
      ))}
    </div>
  );
};

const LeftContentSection = ({ product }) => {
  const embedUrl = getEmbedUrl(product?.video);

  return (
    <div className="mt-6 space-y-6">
      {/* Description */}
      <div className="border-t border-slate-100 pt-6">
        <div className="mb-3 flex items-center justify-between gap-2">
          <h2 className="text-sm font-bold text-slate-950">Product Details</h2>
          <span className="text-xs font-bold text-slate-400">Verified listing</span>
        </div>
        {product?.description ? (
          <div
            className="prose max-w-none text-sm leading-6 text-slate-600"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(product.description) }}
          />
        ) : (
          <p className="text-sm text-slate-500">Product description will be updated soon.</p>
        )}
      </div>

      {/* Detail images — full-width stacked, Temu-style */}
      {product?.detailImages?.length > 0 && (
        <div className="border-t border-slate-100 pt-6">
          {product.detailImages.map((img, idx) => (
            <img
              key={idx}
              src={img}
              alt={`Product detail ${idx + 1}`}
              loading="lazy"
              className="block w-full object-contain"
            />
          ))}
        </div>
      )}

      {/* Detailed description */}
      {product?.detailedDescription && (
        <div className="border-t border-slate-100 pt-6">
          <div
            className="prose max-w-none text-sm leading-6 text-slate-600"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(product.detailedDescription) }}
          />
        </div>
      )}

      {/* Video */}
      {product?.video && (
        <div className="border-t border-slate-100 pt-6">
          <h2 className="mb-3 text-sm font-bold text-slate-950">Product Video</h2>
          <div className="overflow-hidden rounded-xl bg-black">
            {embedUrl ? (
              <iframe
                className="aspect-video w-full"
                src={embedUrl}
                title="Product video"
                style={{ border: 0 }}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            ) : (
              <video controls className="w-full bg-black" src={product.video} />
            )}
          </div>
        </div>
      )}

      {/* Specifications */}
      <div className="border-t border-slate-100 pt-6">
        <h2 className="mb-3 text-sm font-bold text-slate-950">Specifications</h2>
        <LeftSpecifications product={product} />
      </div>

      {/* Shipping info */}
      <div className="border-t border-slate-100 pt-6">
        <h2 className="mb-3 text-sm font-bold text-slate-950">Shipping &amp; Returns</h2>
        <div className="grid gap-4 mds:grid-cols-3">
          {[
            [FaTruck, 'Fast UAE delivery', 'Standard and express options available.'],
            [FaUndoAlt, 'Return support', 'Eligible products include return support.'],
            [FaShieldAlt, 'Protected checkout', 'Payments handled securely.'],
          ].map(([Icon, title, copy]) => (
            <div key={title}>
              <Icon className="mb-2 text-[#E21C1B]" size={14} />
              <p className="text-xs font-bold text-slate-950">{title}</p>
              <p className="mt-1 text-xs leading-5 text-slate-500">{copy}</p>
            </div>
          ))}
        </div>
      </div>

    </div>
  );
};

// ── User Manual / Documents Section ──────────────────────────────────────────

const UserManualSection = ({ product }) => {
  const docs = Array.isArray(product?.document)
    ? product.document.filter(Boolean)
    : product?.document
    ? [product.document]
    : [];

  if (!docs.length) return null;

  return (
    <div className="mt-6 border-t border-slate-100 pt-6">
      <p className="mb-3 text-sm font-bold text-slate-900">Documents</p>
      <div className="divide-y divide-slate-100">
        {docs.map((doc, i) => {
          const url = doc?.url || doc;
          const name = doc?.name || `User Manual${docs.length > 1 ? ` ${i + 1}` : ''}`;
          return (
            <div key={i} className="flex items-center justify-between gap-4 py-3.5">
              <div className="flex min-w-0 items-center gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-slate-50">
                  <FaFileAlt className="text-[#E21C1B]" size={14} />
                </span>
                <div className="min-w-0">
                  <p className="truncate text-sm font-bold text-slate-800">{name}</p>
                  <p className="text-xs text-slate-400">PDF document</p>
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <a
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 transition hover:border-[#E21C1B] hover:text-[#E21C1B]"
                >
                  View
                </a>
                <a
                  href={url}
                  download
                  className="flex items-center gap-1.5 rounded-lg bg-[#E21C1B] px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-[#c01717]"
                >
                  <FaDownload size={10} /> Download
                </a>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ── RIGHT: Sticky Purchase Panel ─────────────────────────────────────────────

const RightPurchasePanel = ({
  product,
  variantDetails,
  selectedVariants,
  groupedVariants,
  setSelectedVariants,
  setVariantDetails,
  setImage,
  finalPrice,
  hasDiscount,
  isLowStock,
  isEnquiryOnly,
  quantity,
  inc,
  dec,
  addCard,
  buyNow,
  addWishlist,
  addMyList,
  emirate,
  setEmirate,
  getShippingInfo,
  loadingShipping,
  shippingInfo,
  onOpenReviews,
  formatBase,
  countdown,
  sellerSectionId = 'seller',
}) => (
  <aside className="bg-white">
    <div className="space-y-3">
      {/* Title + rating */}
      <section>
        <div className="mb-1.5 flex items-start justify-between gap-2">
          <p className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-xs font-bold text-emerald-600">
            <FaBolt size={11} /> Ships in 24h
            {isLowStock && <span className="text-orange-600">&nbsp;· Only a few left</span>}
          </p>
          <ShareButton product={product} compact />
        </div>

        <h1 className="text-[18px] font-black leading-snug text-slate-950 mds:text-[20px]">
          {product?.name}
        </h1>

        {product?.description && (
          <p className="mt-1.5 line-clamp-3 text-xs leading-5 text-slate-500">
            {stripHtml(product.description)}
          </p>
        )}

        <div className="mt-2 flex flex-wrap items-center gap-x-2.5 gap-y-1 text-xs">
          <button type="button" onClick={onOpenReviews} className="flex items-center gap-1 text-[#E21C1B]">
            <span className="flex"><Rating ratings={product?.rating || 0} /></span>
            <span className="font-bold">{product?.rating?.toFixed?.(1) || '0.0'}</span>
          </button>
          <button
            type="button"
            onClick={onOpenReviews}
            className="font-semibold text-slate-500 transition hover:text-[#E21C1B]"
          >
            {product?.reviews?.length || 0} reviews
          </button>
          {product?.brand && (
            <>
              <span className="text-slate-200">|</span>
              <span className="font-semibold text-slate-500">{product.brand}</span>
            </>
          )}
        </div>
      </section>

      {/* Pricing */}
      <section className="border-t border-slate-100 pt-3">
      {product?.sellerType === 'bulk' ? (
        <div>
          <p className="text-xs font-bold uppercase tracking-wide text-slate-400">Bulk price</p>
          <p className="mt-1 text-[30px] font-black leading-none text-[#E21C1B]">
            {product?.bulkContainerPrice ? `From ${formatBase(product.bulkContainerPrice)}` : 'Enquiry Only'}
          </p>
        </div>
      ) : product?.sellerType === 'wholesale' && product?.wholesalePrice ? (
        <div>
          <p className="text-xs font-bold uppercase tracking-wide text-slate-400">Wholesale price</p>
          <p className="mt-1 text-[30px] font-black leading-none text-[#1769e0]">{formatBase(product.wholesalePrice)}</p>
        </div>
      ) : (
        <div>
          <div className="flex flex-wrap items-end gap-2">
            <span className="text-[34px] font-black leading-none text-[#E21C1B]">
              {formatBase(finalPrice)}
            </span>
            {hasDiscount && (
              <>
                <span className="pb-0.5 text-sm font-semibold text-slate-400 line-through">
                  {formatBase(variantDetails.price)}
                </span>
                <span className="mb-0.5 text-xs font-bold text-[#E21C1B]">
                  Save {variantDetails.discount}%
                </span>
              </>
            )}
          </div>
          <p className="mt-1.5 text-xs text-slate-500">
            Includes promotion. Delivery fee shown at checkout.
          </p>
          {hasDiscount && countdown && (
            <p className="mt-1 flex items-center gap-1.5 text-xs font-semibold text-slate-500">
              <FaBolt size={11} className="text-[#E21C1B]" />
              Offer ends in <span className="font-mono font-bold text-slate-700">{countdown}</span>
            </p>
          )}
          {(product?.freeShipping || product?.customization || product?.isWholesaleAvailable) && (
            <div className="mt-2.5 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs">
              {product?.freeShipping && (
                <span className="flex items-center gap-1.5 font-bold text-emerald-700">
                  <FaTruck size={12} /> Free shipping
                </span>
              )}
              {product?.customization && (
                <span className="flex items-center gap-1.5 font-bold text-sky-700">
                  <FaPencilAlt size={11} /> Customization available
                </span>
              )}
              {product?.isWholesaleAvailable && (
                <span className="flex items-center gap-1.5 font-bold text-amber-700">
                  <FaBoxOpen size={12} /> Wholesale available
                </span>
              )}
            </div>
          )}
        </div>
      )}
      </section>

      {/* Variant selectors */}
      {Object.entries(groupedVariants).map(([type, options]) => (
        <section key={type} className="border-t border-slate-100 pt-3">
          <div className="mb-2 flex items-center justify-between gap-2">
            <p className="text-sm font-bold text-slate-950">{type}</p>
            {selectedVariants[type]?.name && (
              <p className="text-xs font-semibold text-slate-500">
                Selected: <span className="text-slate-800">{selectedVariants[type].name}</span>
              </p>
            )}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {options.map((variant, i) => (
              <button
                type="button"
                key={`${variant.name}-${i}`}
                onClick={() => {
                  const nextImages = variant.images?.length ? variant.images : product.images;
                  setSelectedVariants((prev) => ({ ...prev, [type]: variant }));
                  setVariantDetails({
                    price: variant.price || product.price,
                    discount: variant.discount ?? product.discount,
                    stock: variant.stock ?? product.stock,
                    images: nextImages || [],
                  });
                  if (nextImages?.[0]) setImage(nextImages[0]);
                }}
                className={`rounded-full border px-3.5 py-1.5 text-xs font-bold transition hover:-translate-y-0.5 ${
                  selectedVariants[type]?.name === variant.name
                    ? 'border-[#E21C1B] bg-red-50 text-[#E21C1B] shadow-sm'
                    : 'border-slate-200 bg-white text-slate-700 hover:border-[#E21C1B] hover:text-[#E21C1B]'
                }`}
              >
                {variant.name}
              </button>
            ))}
          </div>
        </section>
      ))}

      {/* Item + quantity box */}
      {!isEnquiryOnly && (
        <section className="border-t border-slate-100 pt-3">
          <div className="flex items-center gap-3 rounded-xl border border-slate-200 p-2.5">
            <img
              src={variantDetails.images?.[0] || getProductImage(product)}
              alt=""
              className="h-11 w-11 shrink-0 rounded-lg object-cover"
            />
            <div className="min-w-0 flex-1">
              <p className="text-xs font-bold text-slate-700">Quantity</p>
              <p className="text-[11px] text-slate-400">
                {isLowStock ? `Only ${variantDetails.stock} left` : `Min order: ${product?.minOrderQty || 1}`}
              </p>
            </div>
            <QuantitySelector quantity={quantity} inc={inc} dec={dec} />
          </div>
        </section>
      )}

      {/* Buy buttons */}
      <section className="border-t border-slate-100 pt-3">
        <div className="flex max-w-md items-center gap-2">
          <button
            type="button"
            onClick={buyNow}
            className="flex h-11 flex-1 items-center justify-center rounded-full bg-[#E21C1B] text-sm font-bold text-white shadow-sm transition hover:bg-[#c91413] active:scale-[0.98]"
          >
            {isEnquiryOnly ? 'Enquire Now' : 'Buy Now'}
          </button>
          <button
            type="button"
            onClick={addCard}
            className="flex h-11 flex-1 items-center justify-center gap-1.5 rounded-full border border-[#E21C1B] bg-white text-sm font-bold text-[#E21C1B] transition hover:bg-red-50"
          >
            <RiShoppingCartLine size={16} />
            {isEnquiryOnly ? 'Contact Seller' : 'Add to Cart'}
          </button>
          <button
            type="button"
            onClick={addWishlist}
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-slate-100 text-[#E21C1B] transition hover:bg-red-50"
            aria-label="Add to wishlist"
          >
            <FaHeart size={15} />
          </button>
          <button
            type="button"
            onClick={addMyList}
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-slate-100 text-slate-600 transition hover:bg-blue-50 hover:text-[#1769e0]"
            aria-label="Add to My List"
            title="Add to My List — save for recurring purchases"
          >
            <MdOutlinePlaylistAdd size={22} />
          </button>
        </div>
      </section>

      {/* Shipping badges */}
      <section className="flex flex-wrap items-center gap-x-4 gap-y-1.5 border-t border-slate-100 pt-3 text-xs">
        {product?.freeShipping && (
          <span className="flex items-center gap-1.5 font-bold text-emerald-700">
            <FaCheckCircle size={12} /> Free shipping
          </span>
        )}
        <span className="flex items-center gap-1.5 font-bold text-emerald-700">
          <FaCheckCircle size={12} /> Arrives in 3+ business days
        </span>
      </section>

      {/* Shipping checker */}
      <section className="border-t border-slate-100 pt-3">
        <div className="mb-2.5 flex items-center gap-2">
        <MdLocalShipping className="text-[#E21C1B]" size={17} />
        <h3 className="text-sm font-bold text-slate-950">Delivery info</h3>
        </div>
        <div className="mb-2.5 grid gap-2 mds:grid-cols-2">
        <div>
          <p className="text-xs font-bold text-emerald-700">Standard: Free</p>
          <p className="mt-0.5 text-xs text-slate-600">3-7 business days</p>
        </div>
        <div>
          <p className="text-xs font-bold text-slate-900">Express delivery</p>
          <p className="mt-0.5 text-xs text-[#E21C1B]">1-3 days, extra charges</p>
        </div>
        </div>
        <div className="flex gap-2">
        <input
          value={emirate}
          onChange={(e) => setEmirate(e.target.value)}
          placeholder="Enter emirate or city"
          className="min-w-0 flex-1 rounded-full border border-slate-200 bg-slate-50 px-3.5 py-2.5 text-sm font-semibold outline-none transition focus:border-[#E21C1B] focus:bg-white"
        />
        <button
          type="button"
          onClick={getShippingInfo}
          className="rounded-full bg-slate-950 px-4 py-2.5 text-xs font-bold text-white transition hover:bg-[#E21C1B]"
        >
          {loadingShipping ? '...' : 'Check'}
        </button>
        </div>
        {shippingInfo?.estimatedDelivery && (
          <div className="mt-2.5 rounded-xl bg-emerald-50 p-3 text-xs ring-1 ring-emerald-100">
            <p className="text-slate-700"><strong>Delivery:</strong> {shippingInfo.estimatedDelivery}</p>
            <p className="text-slate-700"><strong>Charge:</strong> {shippingInfo.cost === 0 ? 'Free' : formatBase(shippingInfo.cost)}</p>
          </div>
        )}
      </section>

      {/* Guarantees */}
      <section className="border-t border-slate-100 pt-3">
        <div className="mb-2.5 flex items-center gap-2">
        <FaBoxOpen className="text-[#E21C1B]" size={13} />
        <h3 className="text-sm font-bold text-slate-950">Order guarantee</h3>
        </div>
        <div className="flex flex-wrap gap-1.5">
        {['Easy returns', 'Damage support', 'Delay credit', 'Best price'].map((badge) => (
          <span key={badge} className="flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-bold text-emerald-700">
            <FaCheckCircle size={10} /> {badge}
          </span>
        ))}
        </div>
        <div className="mt-3 flex items-start gap-2">
        <FaShieldAlt className="mt-0.5 shrink-0 text-emerald-600" size={12} />
        <p className="text-xs leading-5 text-slate-500">Secure privacy, protected checkout, trusted payment processing.</p>
        </div>
      </section>

      {/* Seller info */}
      <section id={sellerSectionId} className="border-t border-slate-100 pt-3">
        <p className="mb-2.5 text-xs font-bold uppercase tracking-wide text-slate-400">Sold by</p>
        <div className="flex items-center gap-3">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-slate-950 text-sm font-bold text-white">
          {(product?.shopName || 'P').charAt(0).toUpperCase()}
        </div>
        <div className="min-w-0 flex-1">
          {product?.shopName ? (
            <Link
              to={`/shop/${product.sellerId}`}
              className="inline-flex items-center gap-1.5 font-bold text-slate-950 transition hover:text-[#E21C1B]"
            >
              {product.shopName}
              <MdVerified className="text-emerald-600" size={14} />
            </Link>
          ) : (
            <p className="font-bold text-slate-950">Pikko Dubai Seller</p>
          )}
        </div>
        </div>
        <div className="mt-3 grid grid-cols-3 gap-1.5 border-t border-slate-100 pt-2.5 text-center text-xs">
        {[
          ['98%', 'Positive'],
          ['24h', 'Response'],
          ['UAE', 'Ships from'],
        ].map(([value, label]) => (
          <div key={label}>
            <p className="font-bold text-slate-950">{value}</p>
            <p className="mt-0.5 text-slate-500">{label}</p>
          </div>
        ))}
        </div>
      </section>
    </div>
  </aside>
);

// ── Related products ─────────────────────────────────────────────────────────

const ProductCard = ({ product, addCard, addWishlist, formatBase }) => {
  const price = getFinalPrice(product?.price, product?.discount);
  return (
    <article className="group min-w-[155px] bg-white">
      <Link to={`/product/details/${product?.slug}`} className="block overflow-hidden rounded-lg bg-slate-50">
        <div className="relative aspect-square">
          <img
            src={getProductImage(product)}
            alt={product?.name}
            className="h-full w-full object-cover transition duration-300 group-hover:scale-105"
          />
          {product?.discount > 0 && (
            <span className="absolute left-2 top-2 rounded-full bg-[#E21C1B] px-2 py-1 text-[10px] font-bold text-white">
              -{product.discount}%
            </span>
          )}
        </div>
      </Link>
      <div className="pt-2.5">
        <Link
          to={`/product/details/${product?.slug}`}
          className="line-clamp-2 min-h-[36px] text-xs font-semibold leading-5 text-slate-800 transition hover:text-[#E21C1B]"
        >
          {product?.name}
        </Link>
        <div className="mt-2 flex items-baseline gap-1.5">
          <span className="text-base font-bold text-[#E21C1B]">{formatBase(price || 0)}</span>
          {product?.discount > 0 && (
            <span className="text-xs font-semibold text-slate-400 line-through">{formatBase(product.price)}</span>
          )}
        </div>
        <div className="mt-1 flex items-center gap-1 text-xs">
          <span className="flex"><Rating ratings={product?.rating || 0} /></span>
          <span className="font-semibold text-slate-400">({product?.rating || 0})</span>
        </div>
        <div className="mt-3 grid grid-cols-[1fr_38px] gap-2 opacity-100 transition mds:opacity-0 mds:group-hover:opacity-100">
          <button
            type="button"
            onClick={() => addCard(product?._id)}
            className="rounded-full bg-slate-950 px-3 py-2 text-xs font-bold text-white transition hover:bg-[#E21C1B]"
          >
            Add
          </button>
          <button
            type="button"
            onClick={() => addWishlist(product)}
            className="flex h-9 items-center justify-center rounded-full bg-slate-100 text-[#E21C1B] transition hover:bg-red-50"
            aria-label="Add related product to wishlist"
          >
            <FaRegHeart />
          </button>
        </div>
      </div>
    </article>
  );
};

const RelatedProductsSlider = ({ title, subtitle, products, addCard, addWishlist, formatBase }) => {
  if (!products?.length) return null;
  return (
    <section className="border-t border-slate-100 pt-6">
      <div className="mb-4 flex items-end justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-slate-950">{title}</h2>
          <p className="mt-1 text-sm font-semibold text-slate-500">{subtitle}</p>
        </div>
        <Link
          to="/products"
          className="hidden text-xs font-bold text-slate-700 transition hover:text-[#E21C1B] mds:block"
        >
          View more
        </Link>
      </div>
      <div className="grid auto-cols-[155px] grid-flow-col gap-3 overflow-x-auto pb-2 mds:auto-cols-auto mds:grid-flow-row mds:grid-cols-3 lgs:grid-cols-5">
        {products.slice(0, 15).map((item) => (
          <ProductCard
            key={item?._id || item?.slug}
            product={item}
            addCard={addCard}
            addWishlist={addWishlist}
            formatBase={formatBase}
          />
        ))}
      </div>
    </section>
  );
};

// ── Sticky mobile/tablet buy bar ─────────────────────────────────────────────

const StickyBuyBar = ({ product, image, finalPrice, quantity, isEnquiryOnly, buyNow, formatBase }) => (
  <>
    {/* Mobile bottom bar */}
    <div className="fixed inset-x-0 bottom-0 z-40 border-t border-slate-200 bg-white/95 px-3 py-3 shadow-[0_-12px_35px_rgba(15,23,42,0.12)] backdrop-blur mds:hidden">
      <div className="mx-auto grid max-w-[680px] grid-cols-[44px_1fr_116px] items-center gap-3">
        <img src={image || getProductImage(product)} alt="" className="h-11 w-11 rounded-xl object-cover" />
        <div className="min-w-0">
          <p className="truncate text-xs font-bold text-slate-900">{product?.name}</p>
          <p className="text-sm font-bold text-[#E21C1B]">
            {formatBase(finalPrice)} <span className="text-xs text-slate-400">x {quantity}</span>
          </p>
        </div>
        <button
          type="button"
          onClick={buyNow}
          className="rounded-full bg-[#E21C1B] px-4 py-3 text-xs font-bold text-white shadow-lg shadow-red-100"
        >
          {isEnquiryOnly ? 'Enquire' : 'Buy Now'}
        </button>
      </div>
    </div>
  </>
);

// ── Main page ────────────────────────────────────────────────────────────────

const Details = () => {
  const navigate = useNavigate();
  const { slug } = useParams();
  const dispatch = useDispatch();

  const { product = {}, relatedProducts = [], moreProducts = [] } = useSelector((state) => state.home);
  const { userInfo } = useSelector((state) => state.auth);
  const { errorMessage, successMessage } = useSelector((state) => state.card);
  const { successMessage: myListSuccessMessage, errorMessage: myListErrorMessage } = useSelector((state) => state.myList);
  const { shippingInfo, loadingShipping } = useSelector((state) => state.shipping);
  const { formatBase } = useCurrency();

  const [selectedVariants, setSelectedVariants] = useState({});
  const [image, setImage] = useState('');
  const [variantDetails, setVariantDetails] = useState({ price: 0, discount: 0, stock: 0, images: [] });
  const [quantity, setQuantity] = useState(1);
  const [emirate, setEmirate] = useState('');
  const [reviewsOpen, setReviewsOpen] = useState(false);

  const productSeo = useProductSeo(product);
  const countdown = useCountdown(18000);
  const isEnquiryOnly = product?.sellerType === 'bulk' || product?.enquiryOnly;
  const isLowStock = variantDetails.stock > 0 && variantDetails.stock <= 10;
  const hasDiscount = variantDetails.discount > 0;
  const finalPrice = getFinalPrice(variantDetails.price, variantDetails.discount);

  const groupedVariants = useMemo(
    () =>
      (product?.variants || []).reduce((acc, variant) => {
        if (!acc[variant.type]) acc[variant.type] = [];
        acc[variant.type].push(variant);
        return acc;
      }, {}),
    [product?.variants]
  );

  const similarProducts = useMemo(() => {
    const byId = new Map();
    [...relatedProducts, ...moreProducts].forEach((item) => {
      if (item?._id && item._id !== product?._id) byId.set(item._id, item);
    });
    return [...byId.values()];
  }, [relatedProducts, moreProducts, product?._id]);

  useEffect(() => {
    dispatch(product_details(slug));
    window.scrollTo(0, 0);
  }, [dispatch, slug]);

  useEffect(() => {
    if (successMessage) {
      toast.success(successMessage);
      dispatch(messageClear());
    }
    if (errorMessage) {
      toast.error(errorMessage);
      dispatch(messageClear());
    }
  }, [successMessage, errorMessage, dispatch]);

  useEffect(() => {
    if (myListSuccessMessage) {
      toast.success(myListSuccessMessage);
      dispatch(myListMessageClear());
    }
    if (myListErrorMessage) {
      toast.error(myListErrorMessage);
      dispatch(myListMessageClear());
    }
  }, [myListSuccessMessage, myListErrorMessage, dispatch]);

  useEffect(() => {
    if (!product?._id) return;

    const moq = product.minOrderQty || 1;
    setQuantity(moq);

    if (product.variants?.length) {
      const first = product.variants[0];
      const firstImages = first.images?.length ? first.images : product.images || [];
      setSelectedVariants({ [first.type]: first });
      setVariantDetails({
        price: first.price || product.price,
        discount: first.discount ?? product.discount,
        stock: first.stock ?? product.stock,
        images: firstImages,
      });
      setImage(firstImages?.[0] || product.images?.[0] || '');
    } else {
      setSelectedVariants({});
      setVariantDetails({
        price: product.price || 0,
        discount: product.discount || 0,
        stock: product.stock || 0,
        images: product.images || [],
      });
      setImage(product.images?.[0] || '');
    }
  }, [product]);

  const inc = () => {
    if (!variantDetails.stock || quantity < variantDetails.stock) {
      setQuantity((q) => q + 1);
    } else {
      toast.error('Out of Stock');
    }
  };

  const dec = () => {
    const minimumQty = product.minOrderQty || 1;
    if (quantity > minimumQty) {
      setQuantity((q) => q - 1);
    } else {
      toast.error(`Minimum order quantity is ${minimumQty}`);
    }
  };

  const add_card = () => {
    if (isEnquiryOnly) return openEnquiry();
    return userInfo
      ? dispatch(add_to_card({ userId: userInfo.id, quantity, productId: product._id }))
      : navigate('/login');
  };

  const add_wishlist = () => {
    return userInfo
      ? dispatch(add_to_wishlist({
          userId: userInfo.id,
          productId: product._id,
          name: product.name,
          price: variantDetails.price,
          image: variantDetails.images?.[0],
          discount: variantDetails.discount,
          rating: product.rating,
          slug: product.slug,
        }))
      : navigate('/login');
  };

  const add_my_list = () => {
    if (!userInfo) return navigate('/login');
    dispatch(add_to_my_list({ userId: userInfo.id, productId: product._id }));
  };

  const buynow = () => {
    if (!userInfo) return navigate('/login');
    if (isEnquiryOnly) return openEnquiry();
    return navigate('/shipping', {
      state: {
        products: [{
          sellerId: product.sellerId,
          shopName: product.shopName,
          price: quantity * finalPrice,
          shipping_fee: Number(product.shippingCharge) || 0,
          products: [{ quantity, productInfo: product }],
        }],
        price: finalPrice * quantity,
        shipping_fee: Number(product.shippingCharge) || 0,
        items: 1,
      },
    });
  };

  const getShippingInfo = () => {
    if (!emirate) return toast.error('Please enter an emirate');
    return dispatch(fetch_shipping_info({
      emirate,
      totalPrice: variantDetails.price * quantity,
      FREE_SHIPPING_THRESHOLD,
    }));
  };

  const relatedAddCard = (productId) => {
    if (!productId) return;
    if (userInfo) {
      dispatch(add_to_card({ userId: userInfo.id, quantity: 1, productId }));
    } else {
      navigate('/login');
    }
  };

  const relatedAddWishlist = (item) => {
    if (!item) return;
    if (!userInfo) { navigate('/login'); return; }
    dispatch(add_to_wishlist({
      userId: userInfo.id,
      productId: item._id,
      name: item.name,
      price: item.price,
      image: item.images?.[0],
      discount: item.discount,
      rating: item.rating,
      slug: item.slug,
    }));
  };

  return (
    <div className="min-h-screen bg-white text-slate-900">
      {productSeo && <PageSeo {...productSeo} />}
      <Header />
      <PromoStrip countdown={countdown} />
      <Breadcrumbs product={product} />

      <main className="mx-auto w-[94%] max-w-[1500px] pb-24 pt-4 mds:pb-8 mds:pt-5">
        {/* Two-column Temu-style layout — matches Temu's own PDP, which stays
            side-by-side from tablet width up rather than stacking to a single
            column until true desktop. */}
        <div className="mds:flex mds:items-start">

          {/* ── LEFT: gallery → reviews → content ── */}
          <div className="min-w-0 mds:w-[calc(100%_-_max(50%,300px))] mds:pr-5 lgs:pr-[28px]">
            <ProductGallery
              product={product}
              images={variantDetails.images}
              image={image}
              setImage={setImage}
              hasDiscount={hasDiscount}
              discount={variantDetails.discount}
              isLowStock={isLowStock}
              addWishlist={add_wishlist}
            />

            <div className="border-t border-slate-100 pt-4 mds:hidden">
              <RightPurchasePanel
                product={product}
                variantDetails={variantDetails}
                selectedVariants={selectedVariants}
                groupedVariants={groupedVariants}
                setSelectedVariants={setSelectedVariants}
                setVariantDetails={setVariantDetails}
                setImage={setImage}
                finalPrice={finalPrice}
                hasDiscount={hasDiscount}
                isLowStock={isLowStock}
                isEnquiryOnly={isEnquiryOnly}
                quantity={quantity}
                inc={inc}
                dec={dec}
                addCard={add_card}
                buyNow={buynow}
                addWishlist={add_wishlist}
                addMyList={add_my_list}
                emirate={emirate}
                setEmirate={setEmirate}
                getShippingInfo={getShippingInfo}
                loadingShipping={loadingShipping}
                shippingInfo={shippingInfo}
                onOpenReviews={() => setReviewsOpen(true)}
                formatBase={formatBase}
                countdown={countdown}
                sellerSectionId="seller-mobile"
              />
            </div>

            <RatingSummarySection product={product} onSeeAll={() => setReviewsOpen(true)} />

            <ProductImagesStack
              images={variantDetails.images?.length ? variantDetails.images : product?.images}
            />

            <UserManualSection product={product} />

            <LeftContentSection product={product} />
          </div>

          {/* ── RIGHT: sticky purchase panel ── */}
          <div className="hidden mds:sticky mds:top-3 mds:block mds:w-[50%] mds:min-w-[300px] mds:shrink-0 mds:self-start mds:text-sm lgs:text-base">
            <RightPurchasePanel
              product={product}
              variantDetails={variantDetails}
              selectedVariants={selectedVariants}
              groupedVariants={groupedVariants}
              setSelectedVariants={setSelectedVariants}
              setVariantDetails={setVariantDetails}
              setImage={setImage}
              finalPrice={finalPrice}
              hasDiscount={hasDiscount}
              isLowStock={isLowStock}
              isEnquiryOnly={isEnquiryOnly}
              quantity={quantity}
              inc={inc}
              dec={dec}
              addCard={add_card}
              buyNow={buynow}
              addWishlist={add_wishlist}
              addMyList={add_my_list}
              emirate={emirate}
              setEmirate={setEmirate}
              getShippingInfo={getShippingInfo}
              loadingShipping={loadingShipping}
              shippingInfo={shippingInfo}
              onOpenReviews={() => setReviewsOpen(true)}
              formatBase={formatBase}
              countdown={countdown}
            />
          </div>
        </div>

        {/* Related products — full width below both columns */}
        <div className="mt-6">
          <RelatedProductsSlider
            title="Explore your interests"
            subtitle={`More picks from ${product?.category || 'this category'}.`}
            products={moreProducts?.length ? moreProducts : similarProducts}
            addCard={relatedAddCard}
            addWishlist={relatedAddWishlist}
            formatBase={formatBase}
          />
        </div>
      </main>

      <StickyBuyBar
        product={product}
        image={image}
        finalPrice={finalPrice}
        quantity={quantity}
        isEnquiryOnly={isEnquiryOnly}
        buyNow={buynow}
        formatBase={formatBase}
      />

      <ReviewsModal
        product={product}
        open={reviewsOpen}
        onClose={() => setReviewsOpen(false)}
      />

      <Footer />
    </div>
  );
};

export default Details;
